<template>
  <div class="container-fluid p-0">
    <div class="row g-3">
      <!-- File Upload Card -->
      <div class="col-lg-6 col-md-6">
        <AiChatCard
          :companyId
          :endpoint="`/api/company/${companyId}/portfolio/send-chat`"
          :greeting="$t('ai_chat.greetings.send')"
          @analysis-complete="handleFileAnalysisComplete"
        />
      </div>

      <!-- Order summary -->
      <div class="col-lg-6 col-md-6">
        <div class="card h-100">
          <div class="card-header bg-light py-2">
            <h6 class="card-title mb-0 fw-semibold">
              {{ t("dashboard.portfolio.sendGoOrderSummary") }}
            </h6>
          </div>
          <div class="card-body">
            <form @submit.prevent="openReview" class="d-flex flex-column h-100">
              <CnmcSendPanel
                v-if="cnmc.recipient"
                v-model="cnmc.recipient"
                v-model:aiCompanyName="aiCompanyName"
                :destinations="cnmc.destinations"
              />
              <SendRecipientForm
                v-else
                v-model="recipient"
                v-model:aiCompanyName="aiCompanyName"
                :authority="registry.authority"
                :showInternalAccount="!registry.isTgeSelection"
                :disabled="registry.isBusy"
              >
                <template #external-fields>
                  <div v-if="registry.requiresUnitPrice" class="mb-3">
                    <label for="unitPricePln" class="form-label small">{{
                      t("go_send.unit_price_pln_label")
                    }}</label>
                    <input
                      type="text"
                      inputmode="decimal"
                      class="form-control form-control-sm"
                      :class="{
                        'is-invalid': registry.showUnitPriceValidationError,
                      }"
                      id="unitPricePln"
                      v-model="registry.unitPricePln"
                      :disabled="registry.isBusy"
                    />
                    <div
                      v-if="registry.showUnitPriceValidationError"
                      class="form-text text-danger"
                    >
                      {{ t("validations.unit_price_pln_format") }}
                    </div>
                    <div class="form-text text-muted">
                      {{ t("go_send.unit_price_pln_help") }}
                    </div>
                  </div>
                </template>
              </SendRecipientForm>
            </form>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="hasDisabledCnmcHoldings || promisedCnmcMwh > 0"
      class="row g-3 mt-3"
    >
      <div class="col-12 d-flex flex-column gap-2">
        <div
          v-if="hasDisabledCnmcHoldings"
          class="alert alert-subtle-secondary mb-0"
        >
          {{ t("cnmc.outbound.not_enabled") }}
        </div>
        <div v-if="promisedCnmcMwh > 0" class="alert alert-subtle-info mb-0">
          {{
            t("cnmc.outbound.promised_notice", {
              mwh: formatNumberWithSpaces(promisedCnmcMwh),
            })
          }}
        </div>
      </div>
    </div>

    <!-- Insufficient Inventory Warning -->
    <div v-if="showInsufficientInventoryWarning" class="row g-3 mt-3">
      <div class="col-12">
        <div class="alert alert-warning">
          <FontAwesomeIcon icon="exclamation-triangle" class="me-2" />
          {{
            t("go_send.insufficient_inventory_warning", {
              availableMwh: totalAvailableMwhInInventory,
              neededMwh: totalMwhRequestedFromFile ?? totalSelectedMwh,
            })
          }}
        </div>
      </div>
    </div>

    <!-- Add the PortfolioItemsTable below the chat/summary row -->
    <div class="row g-3 mt-3">
      <div class="col-12">
        <h5 class="mb-3">
          {{ t("dashboard.portfolio.selected_certificates") }}
        </h5>
        <!-- The selection waits for the Spanish rows' annotations; until then nothing could be selected. -->
        <div
          v-if="!holdingsReady"
          class="text-center py-4"
          data-testid="send-holdings-loading"
        >
          <span
            class="spinner-border spinner-border-sm text-body-tertiary"
            role="status"
            :aria-label="t('common.loading')"
          ></span>
        </div>
        <PortfolioItemsTable
          v-else
          ref="portfolioTableRef"
          :selection="selection"
          :enable-count-editing="true"
          :default-visible-columns="TRANSACTION_COLUMNS"
          storage-context="send"
          @update:filtered-stats="onFilteredStats"
        >
          <template #footer-action>
            <button
              type="button"
              class="btn btn-success"
              @click="openReview"
              :disabled="!isOrderComplete || send.isBusy"
            >
              <span
                v-if="send.isBusy"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              {{ t("common.actions.confirm_send") }}
            </button>
          </template>
        </PortfolioItemsTable>
      </div>
    </div>

    <SendGoReviewModal
      v-model="registry.isReviewOpen"
      :recipientCompanyName="recipient.companyName"
      :recipientCountryDisplay="registry.countryDisplay"
      :recipientEecsAccountNumber="recipient.eecsAccountNumber"
      :recipientRegistryInternalAccountNumber="recipient.internalAccountNumber"
      :unitPricePln="registry.submittableUnitPricePln"
      :isHostedRecipient="registry.isHostedRecipient"
      :totalMwh="totalSelectedMwh"
      :technologies="selectedTechnologies"
      :productionMonths="selectedProductionMonths"
      :productionSupport="summarySupport"
      :isLoading="registry.isBusy"
      :confirmDisabled="!isOrderComplete"
      @confirm="confirmRegistrySend"
    />
    <CnmcSendDialogs
      :cnmc
      :company
      @sent="onSent"
      @filing-changed="refetchHoldings"
    />
  </div>
</template>

<script setup lang="ts">
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import {
  type CertificateSelectionCriteria,
  resolveCertificateSelectionFilters,
} from "@/dashboard/inventory_management/common/cancelAiMapping";
import PortfolioItemsTable from "@/dashboard/inventory_management/common/PortfolioItemsTable.vue";
import {
  type SelectableFilterColumn,
  TRANSACTION_COLUMNS,
} from "@/dashboard/inventory_management/common/portfolioTableColumns";
import { usePortfolioItemsStore } from "@/dashboard/inventory_management/common/usePortfolioItemsStore";
import { usePortfolioSelection } from "@/dashboard/inventory_management/common/usePortfolioSelection";
import { useSelectedPortfolioSummary } from "@/dashboard/inventory_management/common/useSelectedPortfolioSummary";
import {
  cnmcSendRules,
  type CnmcHoldingLookup,
  hasDisabledHoldings,
  promisedMwh,
  sendKind,
} from "@/dashboard/inventory_management/send/cnmc/cnmcOutboundRules";
import CnmcSendDialogs from "@/dashboard/inventory_management/send/cnmc/CnmcSendDialogs.vue";
import CnmcSendPanel from "@/dashboard/inventory_management/send/cnmc/CnmcSendPanel.vue";
import { useCnmcHoldings } from "@/dashboard/inventory_management/send/cnmc/useCnmcHoldings";
import { useCnmcOutboundSend } from "@/dashboard/inventory_management/send/cnmc/useCnmcOutboundSend";
import SendGoReviewModal from "@/dashboard/inventory_management/send/SendGoReviewModal.vue";
import {
  EMPTY_SEND_RECIPIENT,
  recipientFromDocument,
  type SendRecipient,
} from "@/dashboard/inventory_management/send/sendRecipient";
import SendRecipientForm from "@/dashboard/inventory_management/send/SendRecipientForm.vue";
import { useRegistrySend } from "@/dashboard/inventory_management/send/useRegistrySend";
import type {
  Company,
  FilteredPortfolioStats,
  FlattenedPortfolioItem,
} from "@/types";
import { groupBy, max, map, sumBy } from "lodash-es";
import {
  computed,
  nextTick,
  onUnmounted,
  ref,
  useTemplateRef,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";
import AiChatCard from "../common/AiChatCard.vue";

const props = defineProps<{
  company: Company;
}>();

const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler, globalWarningHandler } =
  useGlobalHandlers();
const portfolioItemsStore = usePortfolioItemsStore();
const companyId = computed(() => props.company.id!);
// What Send files each Spanish row with, which only this tab fetches.
const { byItemId: cnmcHoldings, ready: holdingsReady } = useCnmcHoldings({
  companyId: () => companyId.value,
  items: () => portfolioItemsStore.allItems,
});
const cnmcHolding: CnmcHoldingLookup = (item) =>
  cnmcHoldings.value.get(item.id);
// One kind per send: registry-API GOs of one authority, or the CNMC GOs of one connection and one
// route. Within a CNMC issuance and plant the selection stays oldest-first, because that is what CNMC
// debits. The selection sees no rows until the Spanish rows are annotated, so a selection restored
// from the URL is read against the annotations rather than dropped as unsendable.
const selection = usePortfolioSelection({
  syncToUrl: true,
  items: computed(() =>
    holdingsReady.value ? portfolioItemsStore.allItems : []
  ),
  ...cnmcSendRules(cnmcHolding),
});
// New annotations can lower what a row allows or move a ladder, so the selection is re-clamped to them.
watch(cnmcHoldings, () => {
  if (!selection.hasSelectedItems) return;
  selection.replaceSelection(
    new Map(
      selection.selectedItemsForApi.map((item) => [
        item.id,
        item.selected_count,
      ])
    )
  );
});
const {
  selectedPortfolioItems,
  totalSelectedMwh,
  selectedTechnologies,
  selectedProductionMonths,
  summarySupport,
} = useSelectedPortfolioSummary(selection);

// One recipient for both modes, so a name typed or read from a document carries over when the
// selection turns out to be Spanish, and one send clears it for the next.
const recipient = ref<SendRecipient>({ ...EMPTY_SEND_RECIPIENT });
const aiCompanyName = ref<string>();
const registry = useRegistrySend({
  selection,
  companyId: () => companyId.value,
  recipient,
});
const cnmc = useCnmcOutboundSend({
  selection,
  companyId: () => companyId.value,
  recipient,
});
const send = computed(() => (cnmc.route ? cnmc : registry));

const totalMwhRequestedFromFile = ref<number | null>(null);
const portfolioTableRef =
  useTemplateRef<InstanceType<typeof PortfolioItemsTable>>("portfolioTableRef");

// Items currently visible after table filters, delivered reactively via `@update:filtered-stats`.
const filteredItems = ref<FlattenedPortfolioItem[]>([]);

const onFilteredStats = (stats: FilteredPortfolioStats) => {
  filteredItems.value = stats.items;
};

const hasDisabledCnmcHoldings = computed(() =>
  hasDisabledHoldings(portfolioItemsStore.availableItems, cnmcHolding)
);
const promisedCnmcMwh = computed(() =>
  promisedMwh(portfolioItemsStore.availableItems, cnmcHolding)
);

// What one send could take: the selection's kind, or the largest kind while nothing is selected. The
// selection gives no reason only for rows of its own kind, and for every sendable row while empty.
const totalAvailableMwhInInventory = computed(
  () =>
    max(
      map(
        groupBy(
          portfolioItemsStore.availableItems.filter(
            (item) => !selection.disabledReason(item)
          ),
          (item) => sendKind(item, cnmcHolding(item))
        ),
        (rows) => sumBy(rows, selection.maxSelectableCount)
      )
    ) ?? 0
);

const showInsufficientInventoryWarning = computed(() => {
  const mwhToCheck = totalMwhRequestedFromFile.value ?? totalSelectedMwh.value;
  return mwhToCheck > 0 && totalAvailableMwhInInventory.value < mwhToCheck;
});

const isOrderComplete = computed(
  () =>
    send.value.isComplete &&
    selectedPortfolioItems.value.length > 0 &&
    totalSelectedMwh.value > 0 &&
    !send.value.isBusy
);

const openReview = () => {
  if (isOrderComplete.value) void send.value.openReview();
};

const confirmRegistrySend = async () => {
  if (isOrderComplete.value && (await registry.confirm())) await onSent();
};

// A send of either mode clears what it filed, so the next one starts empty, and refetches the
// holdings it committed or moved; the new rows refetch their annotations.
const onSent = async () => {
  selection.clearSelection();
  recipient.value = { ...EMPTY_SEND_RECIPIENT };
  aiCompanyName.value = undefined;
  registry.reset();
  cnmc.reset();
  totalMwhRequestedFromFile.value = null;
  await refetchHoldings();
};

const refetchHoldings = async () => {
  try {
    await portfolioItemsStore.refetchItems();
  } catch (error) {
    globalErrorHandler(error);
  }
};

const handleFileAnalysisComplete = async (data: any) => {
  if (!data) {
    return;
  }

  try {
    const formData = data.form_data;

    totalMwhRequestedFromFile.value = null;

    if (formData.recipient_company_name) {
      aiCompanyName.value = formData.recipient_company_name;
    }
    recipient.value = recipientFromDocument(recipient.value, formData);

    const criteria = mapSendCriteriaToSelection(formData?.selection_criteria);
    const totalMwh =
      typeof data.total_mwh_requested === "number"
        ? data.total_mwh_requested
        : null;

    if (criteria || totalMwh) {
      // Apply the AI's document criteria as table filters (leaving any manual filters on other
      // columns intact), then select oldest-first from the resulting filtered set — so the picks
      // honor both the AI's criteria AND the operator's active filters, matching the cancel tab.
      const { selectedMwh, ladderSkipped } = await applyCriteriaAndSelect(
        criteria,
        totalMwh
      );
      if (selectedMwh <= 0) {
        // The criteria matched no available certificates (unavailable technology/device/period, or a
        // stale manual filter), so the table is empty and nothing was selected. Don't claim success —
        // tell the operator to review the criteria instead of leaving a confusing "all set" toast.
        globalWarningHandler(
          t("dashboard.portfolio.fileUpload.analysisNoMatchTitle"),
          withLadderNote(
            t("dashboard.portfolio.fileUpload.analysisNoMatchMessage"),
            ladderSkipped
          )
        );
      } else if (totalMwh && selectedMwh < totalMwh) {
        // Some stock matched, but the filtered inventory can't cover the requested volume — the
        // transfer would be under-filled. Warn instead of reporting success so the operator reviews
        // the shortfall before confirming (the persistent insufficient-inventory banner also shows).
        globalWarningHandler(
          t("dashboard.portfolio.fileUpload.analysisShortfallTitle"),
          withLadderNote(
            t("dashboard.portfolio.fileUpload.analysisShortfallMessage", {
              selected: selectedMwh,
              requested: totalMwh,
            }),
            ladderSkipped
          )
        );
      } else {
        globalSuccessHandler(
          t("dashboard.portfolio.fileUpload.analysisSuccessTitle"),
          t("dashboard.portfolio.fileUpload.analysisSuccessMessage")
        );
      }
    } else {
      // A completed analysis with no selection criteria and no requested volume must still clear any
      // selection and AI-controlled filters left over from a previous document — otherwise
      // usePortfolioSelection carries the prior picks into selectedItemsForApi and could submit
      // certificates from an earlier upload.
      resetAiControlledFilters();
      selection.clearSelection();
    }

    if (totalMwh) {
      totalMwhRequestedFromFile.value = totalMwh;
    }
  } catch (error) {
    globalErrorHandler(error);
  }
};

// The table columns that resolveCertificateSelectionFilters can drive from document criteria. Reset
// these between uploads so one document's criteria never linger as hidden filters on the next.
const AI_CONTROLLED_FILTER_COLUMNS: SelectableFilterColumn[] = [
  "technology_type",
  "production_month",
  "issuing_region",
  "production_device_name",
  "portfolio_name",
];

// Clears every AI-controllable column filter, so a previous upload's criteria never linger. Manual
// filters on other columns are left intact. No-op if the table ref isn't mounted yet.
const resetAiControlledFilters = () => {
  portfolioTableRef.value?.clearFilters(AI_CONTROLLED_FILTER_COLUMNS);
};

// Send's extraction criteria (ISO YYYY-MM-DD periods) reshaped into the shared resolver's
// CertificateSelectionCriteria (YYYY-MM vintage months). Only the columns send extracts are set.
//
// Fail-closed on unparseable production bounds: when a period bound is PRESENT in the raw payload
// but does NOT parse to YYYY-MM, return a criteria object with the unparseable bound so the shared
// resolver `resolveCertificateSelectionFilters` applies its unmatchable-sentinel path (which yields
// an empty table and fires the selectedCount===0 warning, matching the cancel tab's behavior).
const mapSendCriteriaToSelection = (
  raw: any
): CertificateSelectionCriteria | null => {
  if (!raw) {
    return null;
  }
  // Returns the parsed YYYY-MM vintage month if parseable, or the SENTINEL for present-but-unparseable.
  const UNPARSEABLE_SENTINEL = "UNPARSEABLE";
  const toVintageMonth = (value: unknown, fieldName: string): string | null => {
    if (typeof value !== "string") return null;
    const trimmed = value.trim();
    if (!trimmed) return null;
    const match = /^(\d{4})-(\d{2})/.exec(trimmed);
    if (match) {
      return `${match[1]}-${match[2]}`;
    }
    // Present but unparseable: return a sentinel that resolveCertificateSelectionFilters will fail-close on.
    console.warn(
      `[SendGoComponent] ${fieldName} is present but unparseable: "${trimmed}". Failing closed.`
    );
    return UNPARSEABLE_SENTINEL;
  };
  const cleanList = (value: unknown): string[] | null => {
    if (!Array.isArray(value)) return null;
    const cleaned = value
      .map((entry) => (typeof entry === "string" ? entry.trim() : ""))
      .filter((entry): entry is string => !!entry);
    return cleaned.length ? cleaned : null;
  };
  const criteria: CertificateSelectionCriteria = {
    technologyTypes: cleanList(raw.technology_types),
    productionPeriodStart: toVintageMonth(
      raw.production_period_start,
      "production_period_start"
    ),
    productionPeriodEnd: toVintageMonth(
      raw.production_period_end,
      "production_period_end"
    ),
    issuingRegions: null,
    registryRegions: null,
    codAge: null,
    productionSupport: null,
    ecolabels: null,
    productionDeviceNames: cleanList(raw.production_device_names),
    portfolioNames: null,
  };
  return Object.values(criteria).some((value) => value !== null)
    ? criteria
    : null;
};

// What an auto-select took: the MWh selected, and whether a CNMC issuance was passed over because
// an older month CNMC debits first is hidden by the filters.
interface AutoSelection {
  selectedMwh: number;
  ladderSkipped: boolean;
}

// Selects oldest-vintage-first (smaller rows first within a month) from the reactively filtered set,
// so document criteria (applied as table filters) and any manual operator filters are both honored.
// Mirrors the cancel tab. With no MWh target it takes the whole filtered set (mirrors the backend's
// filters-only strategy).
//
// The first row taken fixes the send kind; rows of another kind are skipped. A CNMC row is taken only
// once every older month of its ladder is taken in full, so a ladder whose older months are filtered
// out contributes nothing.
const selectFromFilteredForMwh = (mwhNeeded: number | null): AutoSelection => {
  const { selectedMwh, skipped } = selection.fillOldestFirst(
    filteredItems.value,
    mwhNeeded
  );
  return { selectedMwh, ladderSkipped: skipped };
};

// Returns what was selected (0 MWh when the criteria matched no available certificates).
const applyCriteriaAndSelect = async (
  criteria: CertificateSelectionCriteria | null,
  totalMwh: number | null
): Promise<AutoSelection> => {
  if (criteria && portfolioTableRef.value) {
    const resolved = resolveCertificateSelectionFilters(
      criteria,
      portfolioTableRef.value.getFilterOptions()
    );
    // Reset every AI-controllable column first (a device filter from an earlier document would
    // otherwise silently empty this one's table), then apply this document's resolved set — leaving
    // the operator's manual filters on other columns intact.
    portfolioTableRef.value.clearFilters(AI_CONTROLLED_FILTER_COLUMNS);
    portfolioTableRef.value.applyColumnFilters(resolved.filters);
    // Let the table re-run filtering so `filteredItems` reflects the new criteria before selecting.
    // The table emits update:filtered-stats from a post-render effect, so one flush is enough.
    await nextTick();
  }
  return selectFromFilteredForMwh(totalMwh);
};

const withLadderNote = (message: string, ladderSkipped: boolean) =>
  ladderSkipped
    ? `${message} ${t("cnmc.outbound.auto_select_oldest_note")}`
    : message;

onUnmounted(() => {
  selection.clearSelection();
});
</script>

<style scoped lang="scss"></style>
